// HoldingFormModal — create or edit a holding (position) in an account.
import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_CLASSES = [
  'Cash', 'Australian Equities', 'International Equities', 'Fixed Income',
  'Property', 'Alternatives', 'Private Markets',
];

export default function HoldingFormModal({ clientId, accounts, defaults = {}, initial = {}, onClose, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    accountId: defaults.accountId || initial.accountId || (accounts[0]?.id || ''),
    securityName: initial.securityName || '',
    ticker: initial.ticker || '',
    isin: initial.isin || '',
    assetClass: initial.assetClass || '',
    sector: initial.sector || '',
    region: initial.region || '',
    currency: initial.currency || 'AUD',
    quantity: initial.quantity ?? '',
    unitPrice: initial.unitPrice ?? '',
    marketValue: initial.marketValue ?? '',
    costBase: initial.costBase ?? '',
    asOfDate: initial.asOfDate || new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-compute market value if qty * price entered and market value blank
  const autoMV = () => {
    const q = parseFloat(form.quantity);
    const p = parseFloat(form.unitPrice);
    if (!Number.isNaN(q) && !Number.isNaN(p) && !form.marketValue) {
      setForm((f) => ({ ...f, marketValue: (q * p).toFixed(2) }));
    }
  };

  const save = async () => {
    if (!form.securityName.trim()) { toast.error('Security name is required'); return; }
    if (!form.accountId) { toast.error('Choose an account first — add one if none exist'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: parseFloat(form.quantity) || 0,
        unitPrice: parseFloat(form.unitPrice) || 0,
        marketValue: parseFloat(form.marketValue) || 0,
        costBase: parseFloat(form.costBase) || 0,
      };
      const url = initial.id
        ? `${API}/holdings/${initial.id}`
        : `${API}/investment-accounts/${form.accountId}/holdings`;
      const res = await fetch(url, {
        method: initial.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved?.();
    } catch (e) {
      logger.warn('HoldingFormModal save failed', e);
      toast.error('Could not save holding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="holding-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E0D9]">
          <h3 className="font-heading text-lg text-[#2C2420]">{initial.id ? 'Edit holding' : 'New holding'}</h3>
          <button onClick={onClose} className="text-[#5D524D] hover:text-[#2C2420]" data-testid="holding-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {accounts.length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              No accounts on file. Create an account first, then add holdings to it.
            </p>
          ) : (
            <Field label="Account *">
              <select value={form.accountId} onChange={set('accountId')} className="w-full h-9 border border-[#E6E0D9] rounded-md px-2 text-sm" data-testid="holding-account">
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Security name *">
            <Input value={form.securityName} onChange={set('securityName')} placeholder="e.g. CSL Limited" data-testid="holding-name" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Ticker"><Input value={form.ticker} onChange={set('ticker')} placeholder="CSL.AX" data-testid="holding-ticker" /></Field>
            <Field label="ISIN"><Input value={form.isin} onChange={set('isin')} data-testid="holding-isin" /></Field>
            <Field label="Currency"><Input value={form.currency} onChange={set('currency')} data-testid="holding-currency" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Asset class">
              <select value={form.assetClass} onChange={set('assetClass')} className="w-full h-9 border border-[#E6E0D9] rounded-md px-2 text-sm" data-testid="holding-class">
                <option value="">Select…</option>
                {ASSET_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Sector"><Input value={form.sector} onChange={set('sector')} data-testid="holding-sector" /></Field>
            <Field label="Region"><Input value={form.region} onChange={set('region')} data-testid="holding-region" /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Quantity"><Input type="number" value={form.quantity} onChange={set('quantity')} onBlur={autoMV} data-testid="holding-qty" /></Field>
            <Field label="Unit price"><Input type="number" value={form.unitPrice} onChange={set('unitPrice')} onBlur={autoMV} data-testid="holding-unit" /></Field>
            <Field label="Market value"><Input type="number" value={form.marketValue} onChange={set('marketValue')} data-testid="holding-mv" /></Field>
            <Field label="Cost base"><Input type="number" value={form.costBase} onChange={set('costBase')} data-testid="holding-cost" /></Field>
          </div>
          <Field label="As of date">
            <Input type="date" value={form.asOfDate} onChange={set('asOfDate')} data-testid="holding-date" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-[#E6E0D9] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="holding-modal-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || accounts.length === 0} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="holding-modal-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {initial.id ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5D524D] mb-1 inline-block">{label}</span>
      {children}
    </label>
  );
}
